import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import {
  DollarSign, Users, AlertTriangle, RefreshCcw, Activity,
  CheckCircle2, Clock, XCircle, PlayCircle, Search,
  TrendingUp, Building2, Loader2,
} from 'lucide-react';
// fetchEffectiveMonthlyPrices fue retirado — el monto real está en subscriptions.amount.

/**
 * SuperAdmin · Dashboard de Suscripciones
 * --------------------------------------------------------------
 * Vista 360 del estado de cobros AstroPay de Gastify:
 *   · MRR (Monthly Recurring Revenue) consolidado
 *   · Suscripciones por estado (active / pending / cancelled / dead-letter)
 *   · Pendings vencidos (alerta operativa)
 *   · Cola de retries y eventos de auditoría (payment_events)
 *   · Acciones manuales: reintentar activación, procesar cola ahora
 *
 * Solo accesible para adpenia@gmail.com (validado en RLS).
 */

const STATUS_PILL = {
  active:     { label: 'Activa',     cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  paid:       { label: 'Pagada',     cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  pending:    { label: 'Pendiente',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  cancelled:  { label: 'Cancelada',  cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  expired:    { label: 'Expirada',   cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  dead_letter:{ label: 'Dead-letter',cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  succeeded:  { label: 'OK',         cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  processing: { label: 'En curso',   cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const EVENT_PILL = {
  payment_created:           { color: 'bg-blue-50 text-blue-700',     label: 'Link generado' },
  webhook_received:          { color: 'bg-zinc-50 text-zinc-700',     label: 'Webhook recibido' },
  webhook_invalid_signature: { color: 'bg-rose-50 text-rose-700',     label: '⚠ Firma inválida' },
  activation_success:        { color: 'bg-emerald-50 text-emerald-700', label: '✓ Activación OK' },
  activation_failed:         { color: 'bg-amber-50 text-amber-700',   label: '⚠ Activación falló' },
  retry_attempt:             { color: 'bg-amber-50 text-amber-700',   label: '↻ Reintento' },
  retry_succeeded:           { color: 'bg-emerald-50 text-emerald-700', label: '✓ Retry OK' },
  retry_dead_letter:         { color: 'bg-rose-50 text-rose-700',     label: '✕ Dead-letter' },
  manual_activation:         { color: 'bg-purple-50 text-purple-700', label: '⚡ Manual' },
};

function pill(map, key) {
  return map[key] || { label: key, cls: 'bg-zinc-100 text-zinc-700 border-zinc-200', color: 'bg-zinc-50 text-zinc-700' };
}

function fmtPrice(v, currency = 'ARS') {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(v) || 0);
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}
function hoursSince(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60));
}

export default function SubscriptionsDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingRetries, setProcessingRetries] = useState(false);

  const [subs, setSubs] = useState([]);
  const [retries, setRetries] = useState([]);
  const [events, setEvents] = useState([]);
  const [orgs, setOrgs] = useState([]);

  const [filterOrgId, setFilterOrgId] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    void fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [subsRes, retriesRes, eventsRes, orgsRes] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('id, organization_id, plan_key, plan_name, status, period_start, period_end, amount, currency, paid_at, cancelled_at, created_at, payment_url, organizations(name)')
          .order('created_at', { ascending: false })
          .limit(500),
        supabase
          .from('webhook_retry_queue')
          .select('id, subscription_id, organization_id, plan_key, attempt_count, max_attempts, next_retry_at, last_error, status, created_at, organizations(name)')
          .in('status', ['pending', 'dead_letter', 'processing'])
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('payment_events')
          .select('id, organization_id, subscription_id, event_type, source, signature_valid, error_message, created_at, payload, organizations(name)')
          .order('created_at', { ascending: false })
          .limit(200),
        supabase
          .from('organizations')
          .select('id, name')
          .order('name'),
      ]);

      if (subsRes.error) console.error('subs:', subsRes.error);
      if (retriesRes.error) console.error('retries:', retriesRes.error);
      if (eventsRes.error) console.error('events:', eventsRes.error);
      if (orgsRes.error) console.error('orgs:', orgsRes.error);

      setSubs(subsRes.data || []);
      setRetries(retriesRes.data || []);
      setEvents(eventsRes.data || []);
      setOrgs(orgsRes.data || []);
    } catch (err) {
      console.error('[SubscriptionsDashboard] fetch error:', err);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ── KPIs ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const now = new Date();
    const activeSubs = subs.filter((s) => s.status === 'active' || s.status === 'paid');
    const latestActiveSubsByOrgId = new Map();
    for (const sub of activeSubs) {
      if (!sub?.organization_id || latestActiveSubsByOrgId.has(sub.organization_id)) continue;
      latestActiveSubsByOrgId.set(sub.organization_id, sub);
    }
    const pendingSubs = subs.filter((s) => s.status === 'pending');
    const cancelledThisMonth = subs.filter((s) => {
      if (s.status !== 'cancelled' || !s.cancelled_at) return false;
      const d = new Date(s.cancelled_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });

    // MRR = suma del amount de la última suscripción activa por org.
    // subscriptions.amount es el valor real cobrado (price_monthly del plan al momento del cobro).
    const mrr = Array.from(latestActiveSubsByOrgId.values()).reduce((acc, sub) => {
      return acc + Number(sub.amount ?? 0);
    }, 0);

    const stalePendings = pendingSubs.filter((s) => hoursSince(s.created_at) >= 24);

    const deadLetters = retries.filter((r) => r.status === 'dead_letter');

    return {
      mrr,
      activeCount: activeSubs.length,
      pendingCount: pendingSubs.length,
      cancelledThisMonth: cancelledThisMonth.length,
      stalePendings,
      deadLetters,
      orgsWithSub: new Set(subs.map((s) => s.organization_id)).size,
    };
  }, [subs, retries]);

  const getMonthlyAmount = (row) => Number(row?.amount ?? 0);
  const getMonthlyCurrency = (row) => row?.currency || 'ARS';

  // ── Filtros ─────────────────────────────────────────────────────────
  const filteredEvents = useMemo(() => {
    let xs = events;
    if (filterOrgId !== 'all') xs = xs.filter((e) => e.organization_id === filterOrgId);
    if (filterEvent !== 'all') xs = xs.filter((e) => e.event_type === filterEvent);
    if (search.trim()) {
      const q = search.toLowerCase();
      xs = xs.filter((e) =>
        (e.organizations?.name || '').toLowerCase().includes(q) ||
        (e.error_message || '').toLowerCase().includes(q) ||
        (e.event_type || '').toLowerCase().includes(q)
      );
    }
    return xs.slice(0, 100);
  }, [events, filterOrgId, filterEvent, search]);

  // ── Acciones ────────────────────────────────────────────────────────
  const processRetriesNow = async () => {
    if (processingRetries) return;
    setProcessingRetries(true);
    try {
      // Llamamos via edge function (la RPC es solo service_role).
      // La edge function valida que el caller sea SuperAdmin con su JWT.
      const session = (await supabase.auth.getSession()).data.session;
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-webhook-retries?batch=50`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const summary = await resp.json();
      toast.success(`Procesados: ${summary.processed} (${summary.succeeded} OK, ${summary.rescheduled} reagendados, ${summary.dead_lettered} dead-letter)`);
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error procesando retries');
    } finally {
      setProcessingRetries(false);
    }
  };

  const manualActivate = async (sub) => {
    if (!window.confirm(`¿Activar manualmente la suscripción ${sub.plan_key} para ${sub.organizations?.name || sub.organization_id}?`)) return;
    try {
      const { error } = await supabase.rpc('activate_subscription', {
        p_org_id: sub.organization_id,
        p_subscription_id: sub.id,
        p_plan_key: sub.plan_key,
      });
      if (error) throw error;

      // Insert directo (la policy de superadmin permite FOR ALL).
      // No usamos la RPC porque ahora es solo service_role.
      await supabase.from('payment_events').insert({
        organization_id: sub.organization_id,
        subscription_id: sub.id,
        event_type: 'manual_activation',
        source: 'manual',
        payload: { activated_by_dashboard: true },
      });

      toast.success('Suscripción activada manualmente');
      await fetchAll();
    } catch (err) {
      toast.error(err?.message || 'Error al activar');
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    void fetchAll();
  };

  // ── Render ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-500" />
            Dashboard de Suscripciones
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Estado consolidado de cobros y activaciones · Solo SuperAdmin
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 disabled:opacity-50"
        >
          <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refrescar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={DollarSign}
          color="emerald"
          label="MRR (activos)"
          value={fmtPrice(kpis.mrr)}
          hint={`${kpis.activeCount} suscripciones activas`}
        />
        <KpiCard
          icon={Users}
          color="blue"
          label="Orgs con suscripción"
          value={kpis.orgsWithSub}
          hint={`${subs.length} suscripciones históricas`}
        />
        <KpiCard
          icon={Clock}
          color="amber"
          label="Pendientes"
          value={kpis.pendingCount}
          hint={kpis.stalePendings.length > 0 ? `⚠ ${kpis.stalePendings.length} con +24h` : 'Todo al día'}
        />
        <KpiCard
          icon={XCircle}
          color="rose"
          label="Bajas del mes"
          value={kpis.cancelledThisMonth}
          hint={kpis.deadLetters.length > 0 ? `⚠ ${kpis.deadLetters.length} dead-letters` : '0 dead-letters'}
        />
      </div>

      {/* Alertas: dead-letters */}
      {kpis.deadLetters.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-rose-900">
                {kpis.deadLetters.length} activaciones fallaron tras todos los reintentos
              </h3>
              <p className="text-sm text-rose-700 mt-1">
                Requieren intervención manual. Revisalas en la cola de abajo y activá manualmente desde el listado de suscripciones.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alertas: pendientes vencidos */}
      {kpis.stalePendings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">
                {kpis.stalePendings.length} suscripciones pendientes hace +24h
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Posibles links de pago que el cliente abandonó. Considerá contactar a la org o cancelar.
              </p>
              <ul className="mt-2 text-xs text-amber-700 space-y-1">
                {kpis.stalePendings.slice(0, 5).map((s) => (
                  <li key={s.id}>
                    · <b>{s.organizations?.name || s.organization_id}</b> — {s.plan_key} — {fmtPrice(getMonthlyAmount(s), getMonthlyCurrency(s))} — {hoursSince(s.created_at)}h
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Cola de retries */}
      {retries.length > 0 && (
        <Section
          title="Cola de reintentos"
          icon={RefreshCcw}
          action={
            <button
              onClick={processRetriesNow}
              disabled={processingRetries}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <PlayCircle className="w-4 h-4" />
              {processingRetries ? 'Procesando…' : 'Procesar ahora'}
            </button>
          }
        >
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 border-b">
              <tr>
                <th className="text-left py-2 px-2">Org</th>
                <th className="text-left py-2 px-2">Plan</th>
                <th className="text-left py-2 px-2">Intentos</th>
                <th className="text-left py-2 px-2">Próximo</th>
                <th className="text-left py-2 px-2">Error</th>
                <th className="text-left py-2 px-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {retries.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="py-2 px-2 font-medium">{r.organizations?.name || '—'}</td>
                  <td className="py-2 px-2">{r.plan_key}</td>
                  <td className="py-2 px-2">{r.attempt_count} / {r.max_attempts}</td>
                  <td className="py-2 px-2 text-xs text-zinc-500">{fmtDate(r.next_retry_at)}</td>
                  <td className="py-2 px-2 text-xs text-rose-700 max-w-xs truncate" title={r.last_error}>{r.last_error || '—'}</td>
                  <td className="py-2 px-2">
                    <Pill type={pill(STATUS_PILL, r.status)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Suscripciones recientes */}
      <Section title="Suscripciones recientes" icon={TrendingUp}>
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b">
            <tr>
              <th className="text-left py-2 px-2">Org</th>
              <th className="text-left py-2 px-2">Plan</th>
              <th className="text-left py-2 px-2">Precio mensual</th>
              <th className="text-left py-2 px-2">Período</th>
              <th className="text-left py-2 px-2">Estado</th>
              <th className="text-left py-2 px-2">Creada</th>
              <th className="text-left py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {subs.slice(0, 30).map((s) => (
              <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="py-2 px-2 font-medium flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-zinc-400" />
                  {s.organizations?.name || s.organization_id?.slice(0, 8)}
                </td>
                <td className="py-2 px-2">{s.plan_name || s.plan_key}</td>
                <td className="py-2 px-2">{fmtPrice(getMonthlyAmount(s), getMonthlyCurrency(s))}</td>
                <td className="py-2 px-2 text-xs text-zinc-500">{s.period_start} → {s.period_end}</td>
                <td className="py-2 px-2">
                  <Pill type={pill(STATUS_PILL, s.status)} />
                </td>
                <td className="py-2 px-2 text-xs text-zinc-500">{fmtDate(s.created_at)}</td>
                <td className="py-2 px-2">
                  {s.status === 'pending' && (
                    <button
                      onClick={() => manualActivate(s)}
                      className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100"
                      title="Activar manualmente (sin pago real)"
                    >
                      Activar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Auditoría: payment_events */}
      <Section title="Auditoría de eventos" icon={Activity}>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por org / error / tipo…"
              className="flex-1 px-3 py-1.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <select
            value={filterOrgId}
            onChange={(e) => setFilterOrgId(e.target.value)}
            className="px-3 py-1.5 text-sm border border-zinc-200 rounded-lg"
          >
            <option value="all">Todas las orgs</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            className="px-3 py-1.5 text-sm border border-zinc-200 rounded-lg"
          >
            <option value="all">Todos los eventos</option>
            {Object.keys(EVENT_PILL).map((k) => (
              <option key={k} value={k}>{EVENT_PILL[k].label}</option>
            ))}
          </select>
        </div>

        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b">
            <tr>
              <th className="text-left py-2 px-2">Cuándo</th>
              <th className="text-left py-2 px-2">Org</th>
              <th className="text-left py-2 px-2">Evento</th>
              <th className="text-left py-2 px-2">Origen</th>
              <th className="text-left py-2 px-2">Firma</th>
              <th className="text-left py-2 px-2">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((e) => {
              const p = pill(EVENT_PILL, e.event_type);
              return (
                <tr key={e.id} className="border-b border-zinc-100">
                  <td className="py-2 px-2 text-xs text-zinc-500">{fmtDate(e.created_at)}</td>
                  <td className="py-2 px-2 text-xs">{e.organizations?.name || '—'}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${p.color || 'bg-zinc-50 text-zinc-700'}`}>
                      {p.label}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-xs text-zinc-600">{e.source}</td>
                  <td className="py-2 px-2">
                    {e.signature_valid === true && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                    {e.signature_valid === false && <XCircle className="w-4 h-4 text-rose-500" />}
                    {e.signature_valid === null && <span className="text-zinc-300 text-xs">—</span>}
                  </td>
                  <td className="py-2 px-2 text-xs text-zinc-600 max-w-md truncate" title={e.error_message || ''}>
                    {e.error_message || (e.payload?.status ? `status: ${e.payload.status}` : '')}
                  </td>
                </tr>
              );
            })}
            {filteredEvents.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-sm text-zinc-400">
                  No hay eventos con esos filtros
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────

function KpiCard({ icon: Icon, color, label, value, hint }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    blue:    'bg-blue-50 text-blue-600 border-blue-100',
    amber:   'bg-amber-50 text-amber-600 border-amber-100',
    rose:    'bg-rose-50 text-rose-600 border-rose-100',
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg border ${colorMap[color] || colorMap.blue}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold text-zinc-900">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
      {hint && <div className="text-xs text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
        <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-zinc-500" />}
          {title}
        </h2>
        {action}
      </div>
      <div className="overflow-x-auto p-3">
        {children}
      </div>
    </div>
  );
}

function Pill({ type }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${type.cls}`}>
      {type.label}
    </span>
  );
}
